import React, { useEffect, useState } from 'react';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import styles from './RelatedCompaniesNetwork.module.scss';
import { BLACK_DROPDOWN_CARET, caretRight, purpleOfficeIcon, purpleRedirectIcon } from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { AICA_REPORT_AVAILABLE_TOOLTIP } from '../../../../../../utils/constants/commonConstants';
import clsx from 'clsx';
import { CompaniesNetworkData, RelatedCompany, StatusOption } from './types';

interface RelatedCompaniesNetworkDrawerProps {
    data: CompaniesNetworkData;
    statusOptions: StatusOption[];
    onClose: () => void;
}

const RelatedCompaniesNetworkDrawer: React.FC<RelatedCompaniesNetworkDrawerProps> = ({ data, statusOptions, onClose }) => {
    const [vendorsExpanded, setVendorsExpanded] = useState(true);
    const [customersExpanded, setCustomersExpanded] = useState(true);
    const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

    const vendorCount = data.vendorFor?.length || 0;
    const customerCount = data.customerOf?.length || 0;

    useEffect(() => {
        setVendorsExpanded(true);
        setCustomersExpanded(true);
    }, []);

    const getStatusMeta = (status?: string) => {
        if (!status) return null;
        return statusOptions.find((option) => option?.value?.toLowerCase?.() === status?.toLowerCase?.()) || null;
    };

    const getRedirectUrl = (orgId: string) => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/investor/app/portfolio/${orgId}?fetchType=all&page=1&type=scan&tab=aicaReport&selectedTab=overview`;
    };

    const renderRow = (entity: RelatedCompany, rowId: string) => {
        const statusMeta = getStatusMeta(entity.orgStatus);

        return (
            <div key={rowId} className={styles.DrawerTableRow}>
                <CompanyLinkCell
                    entity={entity}
                    rowId={rowId}
                    hoveredRowId={hoveredRowId}
                    setHoveredRowId={setHoveredRowId}
                    getRedirectUrl={getRedirectUrl}
                />
                <StatusCell statusMeta={statusMeta} fallbackStatus={entity.orgStatus} />
            </div>
        );
    };

    return (
        <SideDrawer onClose={onClose} width="55%">
            <div className={styles.DrawerContent}>
                <div className={styles.DrawerHeader}>
                    <div className={styles.DrawerTitle}>Related Entities in Network</div>
                </div>
                <div className={styles.DrawerTable}>
                    <div className={clsx(styles.DrawerTableRow, styles.DrawerTableHead)}>
                        <div className={styles.NameCol}>Related Party Name</div>
                        <div className={styles.StatusCol}>Status</div>
                    </div>

                    <SectionToggleButton
                        expanded={vendorsExpanded}
                        label={`Vendors for (${vendorCount})`}
                        onToggle={() => setVendorsExpanded((prev) => !prev)}
                        disabled={vendorCount === 0}
                    />
                    {vendorsExpanded && (data.vendorFor || []).map((vendor, index) => renderRow(vendor, `vendor-${index}`))}

                    <SectionToggleButton
                        expanded={customersExpanded}
                        label={`Customers for (${customerCount})`}
                        onToggle={() => setCustomersExpanded((prev) => !prev)}
                        disabled={customerCount === 0}
                    />
                    {customersExpanded && (data.customerOf || []).map((customer, index) => renderRow(customer, `customer-${index}`))}
                </div>
            </div>
        </SideDrawer>
    );
};

export default RelatedCompaniesNetworkDrawer;

type CompanyLinkCellProps = {
    entity: RelatedCompany;
    rowId: string;
    hoveredRowId: string | null;
    setHoveredRowId: (rowId: string | null) => void;
    getRedirectUrl: (orgId: string) => string;
};

const CompanyLinkCell: React.FC<CompanyLinkCellProps> = ({ entity, rowId, hoveredRowId, setHoveredRowId, getRedirectUrl }) => (
    <div className={styles.NameCol}>
        {entity.orgId ? (
            <Tooltip content={AICA_REPORT_AVAILABLE_TOOLTIP} direction="top" usePortal={true} fitContent={true}>
                <a
                    className={styles.DrawerCompanyLink}
                    href={getRedirectUrl(entity.orgId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={() => setHoveredRowId(rowId)}
                    onMouseLeave={() => setHoveredRowId(null)}
                >
                    <div className={styles.DrawerCompanyNameCell}>
                        <img src={hoveredRowId === rowId ? purpleRedirectIcon : purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                        <span className={styles.DrawerCompanyName}>{entity.orgName || '-'}</span>
                    </div>
                </a>
            </Tooltip>
        ) : (
            <div className={styles.DrawerCompanyNameCell}>
                <img src={purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                <span className={styles.DrawerCompanyName}>{entity.orgName || '-'}</span>
            </div>
        )}
    </div>
);

type StatusCellProps = {
    statusMeta: StatusOption | null;
    fallbackStatus?: string;
};

const StatusCell: React.FC<StatusCellProps> = ({ statusMeta, fallbackStatus }) => (
    <div className={styles.StatusCol}>
        {statusMeta ? (
            <span
                className={styles.StatusPill}
                style={{
                    backgroundColor: statusMeta.backgroundColor,
                    color: statusMeta.labelColor,
                }}
            >
                {statusMeta.label}
            </span>
        ) : (
            <span className={clsx(styles.StatusPill, styles.StatusPillFallback)}>{fallbackStatus || '-'}</span>
        )}
    </div>
);

type SectionToggleButtonProps = {
    expanded: boolean;
    label: string;
    onToggle: () => void;
    disabled?: boolean;
};

const SectionToggleButton: React.FC<SectionToggleButtonProps> = ({ expanded, label, onToggle, disabled = false }) => (
    <button type="button" className={styles.SectionRow} onClick={disabled ? undefined : onToggle} disabled={disabled}>
        <span className={styles.SectionCaret}>
            {!disabled && (expanded ? <img src={BLACK_DROPDOWN_CARET} alt="" /> : <img src={caretRight} alt="" />)}
        </span>
        <span>{label}</span>
    </button>
);
